import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CanonicalTag from './components/CanonicalTag';
import LandingPage from './pages/LandingPage';
import BrowsePage from './pages/BrowsePage';
import ItemDetailPage from './pages/ItemDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ListItemPage from './pages/ListItemPage';
import DashboardPage from './pages/DashboardPage';
import MessagesPage from './pages/MessagesPage';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AdminPage from './pages/AdminPage';
import MembershipPage from './pages/MembershipPage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import ReferralPage from './pages/ReferralPage';
import DisputesPage from './pages/DisputesPage';
import './App.css';

// Layout component with header and footer
const MainLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

// Auth layout without header/footer
const AuthLayout = ({ children }) => (
  <div className="min-h-screen">{children}</div>
);

// Router that checks for Google OAuth session_id in URL hash
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  return (
    <Routes>
      {/* Auth Pages */}
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      <Route path="/forgot-password" element={<AuthLayout><ForgotPasswordPage /></AuthLayout>} />
      <Route path="/reset-password" element={<AuthLayout><ResetPasswordPage /></AuthLayout>} />

      {/* Main Pages */}
      <Route path="/" element={<MainLayout><LandingPage /></MainLayout>} />
      <Route path="/browse" element={<MainLayout><BrowsePage /></MainLayout>} />
      <Route path="/items/:id" element={<MainLayout><ItemDetailPage /></MainLayout>} />
      <Route path="/list-item" element={<MainLayout><ListItemPage /></MainLayout>} />
      <Route path="/dashboard" element={<MainLayout><DashboardPage /></MainLayout>} />
      <Route path="/messages" element={<MainLayout><MessagesPage /></MainLayout>} />
      <Route path="/profile" element={<MainLayout><ProfilePage /></MainLayout>} />
      <Route path="/payment-success" element={<MainLayout><PaymentSuccessPage /></MainLayout>} />
      <Route path="/membership" element={<MainLayout><MembershipPage /></MainLayout>} />
      <Route path="/referral" element={<MainLayout><ReferralPage /></MainLayout>} />
      <Route path="/disputes" element={<MainLayout><DisputesPage /></MainLayout>} />

      {/* Admin */}
      <Route path="/admin" element={<MainLayout><AdminPage /></MainLayout>} />

      {/* 404 */}
      <Route path="*" element={<MainLayout><LandingPage /></MainLayout>} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid #EBE8DF',
              borderRadius: '12px',
              color: '#2C3329'
            }
          }}
        />
        <CanonicalTag />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
